import { LEAVE_DETAILS_FAILURE, LEAVE_DETAILS_SUCCESS, LEAVE_SET_ERROR, LEAVE_SET_LOADING } from "../Leave/LeaveActionType";
import {
    EMPLOYEE_DETAILS_SUCCESS,
    EMPLOYEE_DETAILS_FAILURE,
    EMPLOYEE_DETAILS_RESET,
    EMPLOYEE_SET_LOADING,
    EMPLOYEE_SET_ERROR,
    EMPLOYEE_CLEAR_ERROR,
    EMPLOYEE_MANAGE_FAILURE,
    EMPLOYEE_MANAGE_SUCCESS,
    EMPLOYEE_MANAGE_REQUEST
} from "./EmployeeActionType";

const apiConfig = window as any;

/* ------------------- BASE API URL ------------------- */

const BASE_API_URL =
    apiConfig.BASE_API_URL?.charAt(apiConfig.BASE_API_URL.length - 1) === "/"
        ? apiConfig.BASE_API_URL.substring(0, apiConfig.BASE_API_URL.length - 1)
        : apiConfig.BASE_API_URL;

const BasicAuthentication = apiConfig.BasicAuthentication;

/* ------------------- HEADERS ------------------- */

const postAuthorizedHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(BasicAuthentication
        ? { Authorization: "Basic " + btoa(BasicAuthentication) }
        : {})
};

/* ============================================================
   INTERFACES
============================================================ */

export interface EmployeeFilterRequest {
    Department?: string;
    Status?: string;
    EmploymentType?: string;
    Designation?: string;
    JoinDateFrom?: string;
    JoinDateTo?: string;
    SearchTerm?: string;
}

export interface EmployeeRecord {
    id: string;
    employeeCode: string;
    name: string;
    email: string;
    phone: string;
    department: string;
    designation: string;
    employmentType: string;
    joinDate: string;
    status: string;
}

export interface AllEmployeeDetailsDto {
    rowNum: number;
    totalCount: number;
    id: number;
    employeeId: number;
    employeeCode: string;
    firstName: string;
    lastName: string;
    employeeName: string;
    email: string;
    mobileNumber: string;
    dateOfBirth: string;
    age: number;
    gender: string;
    employeeAddress: string;
    addressLine2: string;
    city: string;
    state: string;
    pincode: string;
    fullAddress: string;
    department: string;
    designation: string;
    joinDate: string;
    employmentStatus: string;
    isActive: boolean;
    status: string;
    experienceYears: number;
    emergencyContactName: string;
    emergencyContactNumber: string;
    relationship: string;
    photoPath: string;
    roleId: number;
    roleName: string;
    createdOn: string;
    updatedOn: string;
    updatedBy: string;
    displayName: string;
    displayCode: string;
    joinDateFormatted: string;
    dateOfBirthFormatted: string;
    employeeAge: string;
}

export interface EmployeeApiResponse {
    success: boolean;
    message: string;
    records: AllEmployeeDetailsDto[];
}

export interface PaginatedEmployeeResponse {
    currentPage: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
    data: AllEmployeeDetailsDto[];
}

// Complete ManageEmployeeRequest interface for all operations
export interface ManageEmployeeRequest {
    indicator: string; // "I" for Insert, "U" for Update, "D" for Delete
    employeeId?: number | null;
    employeeCode?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    employeeName?: string | null;
    email?: string | null;
    mobileNumber?: string | null;
    dateOfBirth?: string | null;
    employeeAddress?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    state?: string | null;
    pincode?: string | null;
    department?: string | null;
    designation?: string | null;
    joinDate?: string | null;
    employmentStatus?: string | null;
    isActive?: boolean | null;
    emergencyContactName?: string | null;
    emergencyContactNumber?: string | null;
    relationship?: string | null;
    photoPath?: string | null;
    roleId?: number | null;
    password?: string | null;
    updatedBy?: string | null;
}

export interface ManageEmployeeResponse {
    success: boolean;
    message: string;
    id: number;
    employeeId: number;
    employeeCode: string;
    operation: string;
}

export interface LeaveRecord {
    employeeExists: boolean;
    employeeName: string;
    employeeDepartment: string;
    employeeDesignation: string;
    message: string;
    id: number;
    leaveRequestId: string;
    employeeId: number;
    employeeCode: string;
    leaveType: string;
    fromDate: string;
    toDate: string;
    days: number;
    status: string;
    appliedOn: string;
    reason: string;
    createdOn: string;
    updatedOn: string;
    updatedBy: string | null;
}

export interface LeaveApiResponse {
    isSuccess: boolean;
    message: string;
    accessedOn: string | null;
    records: LeaveRecord[];
}

export interface FinalLeaveApproverRequest {
    leaveRequestId: string;
    hrEmployeeCode: string;
    employeeCode: string;
    status: string;
    rejectionReason?: string;
}

export interface FinalLeaveApproverResponse {
    isSuccess: boolean;
    message: string;
    accessedOn: string | null;
    records: any[];
}

/* ============================================================
   GET ALL EMPLOYEES
============================================================ */

/* ============================================================
   GET ALL EMPLOYEES - FIXED VERSION
============================================================ */

export const GetAllEmployees = () => async (dispatch: any) => {
    dispatch({ type: EMPLOYEE_SET_LOADING, payload: true });

    try {
        const url = `${BASE_API_URL}/AllEmployees`;

        const response = await fetch(url, {
            method: "GET",
            headers: postAuthorizedHeaders
        });

        const data = await response.json();

        dispatch({ type: EMPLOYEE_SET_LOADING, payload: false });

        // Check if the response has isSuccess and records array
        if (data && data.isSuccess === true && Array.isArray(data.records)) {
            
            dispatch({
                type: EMPLOYEE_DETAILS_SUCCESS,
                payload: data.records
            });

            return { success: true, records: data.records };
        }
        // Check if the response is an array directly (fallback)
        else if (Array.isArray(data)) {
            
            dispatch({
                type: EMPLOYEE_DETAILS_SUCCESS,
                payload: data
            });

            return { success: true, records: data };
        }
        // Handle other cases
        else {
            
            dispatch({
                type: EMPLOYEE_DETAILS_FAILURE,
                payload: data?.message || "Failed to fetch employee details: Unexpected response format"
            });

            return { success: false, records: [] };
        }

    } catch (err) {
        console.error("GetAllEmployees error:", err);

        dispatch({ type: EMPLOYEE_SET_LOADING, payload: false });

        dispatch({
            type: EMPLOYEE_SET_ERROR,
            payload: {
                success: false,
                message: "Failed to fetch employee details",
                error: err
            }
        });

        return { success: false, records: [] };
    }
};
/* ============================================================
   SINGLE METHOD FOR ALL EMPLOYEE OPERATIONS (I/U/D)
   Uses only indicator field - no separate operation type
============================================================ */

export const ManageEmployee =
    (employeeData: ManageEmployeeRequest) => async (dispatch: any) => {

        dispatch({ 
            type: EMPLOYEE_MANAGE_REQUEST, 
            payload: { 
                indicator: employeeData.indicator 
            } 
        });
        
        dispatch({ type: EMPLOYEE_SET_LOADING, payload: true });

        try {
            const url = `${BASE_API_URL}/EmployeeUpdate`;
            
            const response = await fetch(url, {
                method: "POST",
                headers: postAuthorizedHeaders,
                body: JSON.stringify(employeeData)
            });

            const data = await response.json();

            dispatch({ type: EMPLOYEE_SET_LOADING, payload: false });

            if (response.ok && data?.success) {
                dispatch({
                    type: EMPLOYEE_MANAGE_SUCCESS,
                    payload: {
                        message: data.message,
                        operation: data.operation,
                        id: data.id,
                        employeeId: data.employeeId,
                        employeeCode: data.employeeCode
                    }
                });

                setTimeout(() => {
                    dispatch(GetAllEmployees() as any);
                }, 100);

                return {
                    success: true,
                    data,
                    message: data.message
                };
            }

            // Handle error cases with more detail
            const errorMessage = data?.message || data?.error || `HTTP Error ${response.status}`;
            console.error("❌ API Error:", {
                status: response.status,
                statusText: response.statusText,
                data: data
            });

            const errorPayload = {
                success: false,
                message: errorMessage,
                error: data,
                status: response.status
            };

            dispatch({
                type: EMPLOYEE_SET_ERROR,
                payload: errorPayload
            });

            dispatch({
                type: EMPLOYEE_MANAGE_FAILURE,
                payload: errorPayload
            });

            return {
                success: false,
                message: errorMessage,
                status: response.status,
                data: data
            };
            
        } catch (err) {
            console.error("ManageEmployee network error:", err);

            dispatch({ type: EMPLOYEE_SET_LOADING, payload: false });

            const errorPayload = {
                success: false,
                message: "Failed to process employee request",
                error: err
            };

            dispatch({
                type: EMPLOYEE_SET_ERROR,
                payload: errorPayload
            });

            dispatch({
                type: EMPLOYEE_MANAGE_FAILURE,
                payload: errorPayload
            });

            return {
                success: false,
                message: "Failed to process employee request",
                error: err
            };
        }
    };

/* ============================================================
   FILTER ACTIONS
============================================================ */

export const SetEmployeeFilters = (filters: EmployeeFilterRequest) => ({
    type: "EMPLOYEE_SET_FILTERS",
    payload: filters
});

export const ClearEmployeeFilters = () => ({
    type: "EMPLOYEE_CLEAR_FILTERS"
});

/* ============================================================
   SELECT EMPLOYEE RECORD
============================================================ */

export const SetSelectedEmployee = (employeeRecord: AllEmployeeDetailsDto | null) => ({
    type: "EMPLOYEE_SET_SELECTED",
    payload: employeeRecord
});

/* ============================================================
   RESET EMPLOYEE STATE
============================================================ */

export const ResetEmployeeDetails = () => (dispatch: any) => {
    dispatch({ type: EMPLOYEE_DETAILS_RESET });
};

/* ============================================================
   CLEAR ERROR
============================================================ */

export const ClearEmployeeError = () => ({
    type: EMPLOYEE_CLEAR_ERROR
});

/* ============================================================
   LEAVE MANAGEMENT ACTIONS (Keep as is)
============================================================ */

export const ManageLeaveApproval =
    (approvalData: any) => async (dispatch: any) => {
        dispatch({ type: "LEAVE_MANAGE_REQUEST", payload: { operation: approvalData.status } });

        try {
            const url = `${BASE_API_URL}/LeaveApprover`;

            const response = await fetch(url, {
                method: "POST",
                headers: postAuthorizedHeaders,
                body: JSON.stringify(approvalData)
            });

            const data = await response.json();

            if (data?.isSuccess) {
                dispatch({
                    type: "LEAVE_MANAGE_SUCCESS",
                    payload: {
                        message: data.message,
                        operation: approvalData.status,
                        response: data
                    }
                });

                return { success: true, ...data };
            }

            const errorPayload = {
                success: false,
                message: data.message || "Failed to process leave approval",
                error: data
            };

            dispatch({
                type: "LEAVE_MANAGE_FAILURE",
                payload: errorPayload
            });

            return { success: false, ...data };
        } catch (err) {
            console.error("ManageLeaveApproval error:", err);

            const errorPayload = {
                success: false,
                message: "Failed to process leave approval",
                error: err
            };

            dispatch({
                type: "LEAVE_MANAGE_FAILURE",
                payload: errorPayload
            });

            return { success: false, message: "Failed to process leave approval" };
        }
    };

export const GetLeaveDetails =
    (employeeCode?: string) => async (dispatch: any) => {
        dispatch({ type: LEAVE_SET_LOADING, payload: true });

        try {
            const params = new URLSearchParams();
            if (employeeCode) {
                params.append('employeeCode', employeeCode);
            }

            const queryString = params.toString();
            const url = `${BASE_API_URL}/LeaveDetails${queryString ? `?${queryString}` : ''}`;


            const response = await fetch(url, {
                method: "GET",
                headers: postAuthorizedHeaders
            });

            const data: LeaveApiResponse = await response.json();

            dispatch({ type: LEAVE_SET_LOADING, payload: false });

            if (data?.isSuccess) {
                const records = data.records || [];

                dispatch({
                    type: LEAVE_DETAILS_SUCCESS,
                    payload: records
                });

                return { success: true, records };
            }

            dispatch({
                type: LEAVE_DETAILS_FAILURE,
                payload: data.message || "Failed to fetch leave details"
            });

            return { success: false, records: [] };
        } catch (err) {
            console.error("GetLeaveDetails error:", err);

            dispatch({ type: LEAVE_SET_LOADING, payload: false });

            dispatch({
                type: LEAVE_SET_ERROR,
                payload: {
                    success: false,
                    message: "Failed to fetch leave details",
                    error: err
                }
            });

            return { success: false, records: [] };
        }
    };

export const FinalLeaveApprover =
    (approvalData: FinalLeaveApproverRequest) => async (dispatch: any) => {
        dispatch({ type: "LEAVE_FINAL_APPROVER_REQUEST", payload: { operation: approvalData.status } });

        try {
            const url = `${BASE_API_URL}/FinalLeaveApprover`;

            const response = await fetch(url, {
                method: "POST",
                headers: postAuthorizedHeaders,
                body: JSON.stringify(approvalData)
            });

            const data: FinalLeaveApproverResponse = await response.json();

            if (data?.isSuccess) {
                dispatch({
                    type: "LEAVE_FINAL_APPROVER_SUCCESS",
                    payload: {
                        message: data.message,
                        operation: approvalData.status,
                        response: data
                    }
                });

                setTimeout(() => {
                    dispatch(GetLeaveDetails(approvalData.employeeCode) as any);
                }, 100);

                return { success: true, ...data };
            }

            const errorPayload = {
                success: false,
                message: data.message || "Failed to process final leave approval",
                error: data
            };

            dispatch({
                type: "LEAVE_FINAL_APPROVER_FAILURE",
                payload: errorPayload
            });

            return { success: false, ...data };
        } catch (err) {
            console.error("FinalLeaveApprover error:", err);

            const errorPayload = {
                success: false,
                message: "Failed to process final leave approval",
                error: err
            };

            dispatch({
                type: "LEAVE_FINAL_APPROVER_FAILURE",
                payload: errorPayload
            });

            return { success: false, message: "Failed to process final leave approval" };
        }
    };